import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import React, { useState } from "react";
import { auth, provider_apple } from "../../Config/firebase-config";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import signInHero from "../../Assets/Images/signin-hero.jpg";
import logo from "../../Assets/Images/landing/eww-logo.png";
import "../../Assets/Styles/pages/signIn.scss";
import { useTranslation } from "react-i18next";
import { LoginAPI, Logout } from "../../actions/Auth/Auth";
import { useDispatch } from "react-redux";
import { setUserAuth } from "../../Redux/UserSlice/userSlice";
import { GetAllTemplates } from "../../actions/Template/Template";
import { GetSettings } from "../../actions/Settings/Setting";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

interface SignInProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  setActive?: (v: string) => void;
  onSuccess?: () => void | Promise<void>;
}

// ── tiny inline SVG icons ────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.9 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.4 0-9.9-3.1-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.9 5.1l6.2 5.2C42.4 34.9 44 30 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 384 512" fill="currentColor" aria-hidden>
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.6 84.5c14.9-17.7 22.9-40.5 22-64-20.7 1.2-43 12.3-58.3 30-14.1 16-25.3 39.3-22.6 61.9 22 1.6 46.3-10 58.9-27.9z" />
    </svg>
  );
}

function Spinner({ light = false }: { light?: boolean }) {
  return (
    <span
      className="si-spinner"
      style={{
        borderColor: light ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.25)",
        borderTopColor: "transparent",
      }}
    />
  );
}

// ── main component ───────────────────────────────────────────────────────────
const SignIn = ({ open, setOpen, setActive, onSuccess }: SignInProps) => {
  const [loader, setLoader] = useState<"" | "google" | "apple">("");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const provider_google = new GoogleAuthProvider();
  provider_google.setCustomParameters({ prompt: "select_account" });

  const handlePage = (page: string) => {
    Promise.all([history.push(page), setOpen(false)]);
  };

  const handleClick_google = async () => {
    setLoader("google");
    try {
      const data = await signInWithPopup(auth, provider_google);
      const user = data.user;
      const customs = (user as { reloadUserInfo?: { customAttributes?: string } }).reloadUserInfo || {};
      const customAttributes = customs.customAttributes
        ? JSON.parse(customs.customAttributes)
        : {};
      const payload = {
        photoURL: user.photoURL,
        displayName: user.displayName,
        email: user.email,
      };
      dispatch(setUserAuth(payload));
      if (!customAttributes?.subscription) {
        const resp = (await dispatch(LoginAPI())) as { email?: string } | false;
        if (resp && resp.email) {
          Promise.all([dispatch(GetAllTemplates()), dispatch(GetSettings())]);
          if (setActive) setActive("template");
          setOpen(false);
          await onSuccess?.();
        } else {
          Promise.all([dispatch(Logout())]);
          alert(t("SignInError"));
        }
      } else {
        Promise.all([dispatch(GetAllTemplates()), dispatch(GetSettings())]);
        if (setActive) setActive("template");
        setOpen(false);
        await onSuccess?.();
      }
    } catch (error) {
      console.error("Sign-in error:", error);
      alert(t("SignInError"));
    } finally {
      setLoader("");
    }
  };

  const handleClick_apple = async () => {
    if (loader === "apple") setLoader("");
    else setLoader("apple");
    try {
      provider_apple.addScope("email");
      provider_apple.addScope("name");
      const data = await signInWithPopup(auth, provider_apple);
      const customs = (data.user as { reloadUserInfo?: { customAttributes?: string } }).reloadUserInfo;
      const payload = {
        photoURL: data.user.photoURL,
        displayName: data.user.displayName,
      };
      dispatch(setUserAuth(payload));
      const customAttributes =
        customs && Object.prototype.hasOwnProperty.call(customs, "customAttributes")
          ? JSON.parse(customs.customAttributes)
          : {};
      if (
        !(
          customAttributes &&
          customAttributes.hasOwnProperty("roleName") &&
          customAttributes.hasOwnProperty("env") &&
          process.env.REACT_APP_ENVIRONMENT === customAttributes.env
        )
      ) {
        const resp = (await dispatch(LoginAPI())) as { email?: string } | false;
        if (resp && "email" in resp && resp.email) {
          setOpen(false);
          await onSuccess?.();
        }
      } else {
        setOpen(false);
        await onSuccess?.();
      }
      if (setActive) setActive("template");
    } catch (e) {
      console.log("Firebase error: ", e);
    }
    setLoader("");
    Promise.all([dispatch(GetAllTemplates()), dispatch(GetSettings())]);
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="signin-modal-title"
      aria-describedby="signin-modal-desc"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        style: { backgroundColor: "rgba(15,10,20,0.6)", backdropFilter: "blur(8px)" },
      }}
    >
      <Fade in={open}>
        <div className="si-wrapper">
          {/* ── close button ─────────────────────────────────────────── */}
          <button
            className="si-close-btn"
            onClick={() => setOpen(false)}
            aria-label="Close sign in"
          >
            ✕
          </button>

          {/* ── LEFT: animated hero panel ─────────────────────────── */}
          <div className="si-hero">
            {/* gradient glow orbs */}
            <div className="si-glow si-glow--1" />
            <div className="si-glow si-glow--2" />

            {/* device frame */}
            <div className="si-device-frame">
              <img
                src={signInHero}
                alt=""
                className="si-hero-photo"
              />

              {/* scan sweep line */}
              <div className="si-scan-sweep" />

              {/* diagonal watermark tile */}
              <div className="si-wm-tile" />

              {/* stamped badge */}
              <div className="si-wm-stamp">© eZy WATERMARK</div>

              {/* signature */}
              <div className="si-signature">Alex R.</div>

              {/* progress bar */}
              <div className="si-progress-track">
                <div className="si-progress-fill" />
              </div>
            </div>

            {/* floating chips */}
            <div className="si-chip si-chip--1">© signed</div>
            <div className="si-chip si-chip--2">batch · 128</div>
            <div className="si-chip si-chip--3">template saved</div>

            {/* live indicator */}
            <div className="si-live-row">
              <span className="si-live-dot" />
              Live preview
            </div>
          </div>

          {/* ── RIGHT: sign in panel ──────────────────────────────── */}
          <div className="si-panel">
            <div className="si-panel-inner">
              {/* header */}
              <div className="si-panel-header">
                <img src={logo} alt="" className="si-panel-logo" />
                <span className="si-panel-eyebrow">Welcome back</span>
              </div>

              <h2 className="si-panel-title" id="signin-modal-title">
                Sign in to your account
              </h2>
              <p className="si-panel-sub" id="signin-modal-desc">
                {t("SignIn.SubtextSignin")}
              </p>

              {/* buttons */}
              <div className="si-btns">
                {/* Google */}
                <button
                  className="si-btn si-btn--google"
                  onClick={handleClick_google}
                  disabled={loader !== ""}
                >
                  <span className="si-btn-shimmer" />
                  <span className="si-btn-content">
                    {loader === "google" ? <Spinner /> : <GoogleIcon />}
                    {loader === "google" ? "Signing in…" : t("SignIn.SignGoogle")}
                  </span>
                </button>

                {/* Apple */}
                <button
                  className="si-btn si-btn--apple"
                  onClick={handleClick_apple}
                  disabled={loader !== ""}
                >
                  <span className="si-btn-shimmer" />
                  <span className="si-btn-content">
                    {loader === "apple" ? <Spinner light /> : <AppleIcon />}
                    {loader === "apple" ? "Signing in…" : t("SignIn.SignApple")}
                  </span>
                </button>
              </div>

              {/* legal */}
              <p className="si-legal">
                {t("SignIn.Agreement")}{" "}
                <span
                  className="si-legal-link"
                  onClick={() => handlePage("/terms")}
                >
                  {t("SignIn.Terms")}
                </span>
              </p>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default SignIn;
